// services/transcriptionService.js
import axios from 'axios';

const API_URL =  'https://app.polarcut.io/api';

/**
 * Service to handle interactions with the transcription API
 */
class TranscriptionService {
  /**
   * Get the status of a specific transcription job
   * @param {string} jobName - The name of the transcription job
   * @returns {Promise<Object>} Job status and progress information
   */
  static async getJobStatus(jobName) {
    try {
      const response = await axios.get(`${API_URL}/transcribe/status/${jobName}`);
      return response.data;
    } catch (error) {
      console.error('Error getting transcription job status:', error);
      throw new Error(
        error.response?.data?.error ||
        'Failed to get transcription status. Please try again.'
      );
    }
  }

  /**
   * Poll a job status until it completes or fails
   * @param {string} jobName - The name of the transcription job
   * @param {Function} onProgress - Callback for progress updates
   * @param {number} intervalMs - Polling interval in milliseconds
   * @returns {Promise<Object>} Final job status
   */
  static async pollJobUntilComplete(jobName, onProgress = null, intervalMs = 5000) {
    return new Promise((resolve, reject) => {
      const checkStatus = async () => {
        try {
          const status = await this.getJobStatus(jobName);
          
          // Call progress callback if provided
          if (onProgress && typeof status.progress === 'number') {
            onProgress(status.progress);
          }
          
          if (status.status === 'COMPLETED') {
            resolve(status);
            return;
          } else if (status.status === 'FAILED') {
            reject(new Error(status.error || 'Transcription job failed'));
            return;
          }
          
          // Continue polling
          setTimeout(checkStatus, intervalMs);
        } catch (error) {
          reject(error);
        }
      };
      
      // Start polling
      checkStatus();
    });
  }
}

export default TranscriptionService;